import React, { useId, useMemo, useRef } from "react";
import { joinTags, parseTags } from "./qmsVaultUtils";

function QmsTagsInput({
  id,
  value = "",
  onChange,
  disabled = false,
  placeholder = "Add tags…",
  hint = "Press Enter, comma, or space to add a tag.",
  label,
  className = "",
}) {
  const autoId = useId();
  const inputId = id || `qms-tags-input-${autoId}`;
  const inputRef = useRef(null);
  const [tagInput, setTagInput] = React.useState("");

  const tagList = useMemo(() => parseTags(value), [value]);

  const updateTags = (nextTags) => {
    onChange?.(joinTags(nextTags));
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (!tag || disabled) return;
    if (tagList.includes(tag)) {
      setTagInput("");
      return;
    }
    updateTags([...tagList, tag]);
    setTagInput("");
  };

  const removeTag = (tag) => {
    if (disabled) return;
    updateTags(tagList.filter((item) => item !== tag));
  };

  const focusInput = () => {
    if (!disabled) inputRef.current?.focus();
  };

  return (
    <div className={`qms-tags-input ${className}`.trim()}>
      {label ? (
        <label className="qms-tags-input__label" htmlFor={inputId}>
          {label}
        </label>
      ) : null}
      <div
        className={`qms-tags-input__box${disabled ? " is-disabled" : ""}`}
        onClick={focusInput}
        role="group"
        aria-label={label || "Tags"}
      >
        {tagList.map((tag) => (
          <span key={tag} className="qms-tags-input__chip">
            {tag}
            {!disabled ? (
              <button
                type="button"
                className="qms-tags-input__remove"
                onClick={(event) => {
                  event.stopPropagation();
                  removeTag(tag);
                }}
                aria-label={`Remove ${tag}`}
              >
                ×
              </button>
            ) : null}
          </span>
        ))}
        {!disabled ? (
          <input
            ref={inputRef}
            id={inputId}
            className="qms-tags-input__field"
            value={tagInput}
            placeholder={tagList.length ? "" : placeholder}
            onChange={(event) => setTagInput(event.target.value)}
            onKeyDown={(event) => {
              if (
                event.key === "Enter" ||
                event.key === "," ||
                (event.key === " " && tagInput.trim())
              ) {
                event.preventDefault();
                addTag();
              } else if (event.key === "Backspace" && !tagInput && tagList.length) {
                removeTag(tagList[tagList.length - 1]);
              }
            }}
            onBlur={() => {
              if (tagInput.trim()) addTag();
            }}
            disabled={disabled}
          />
        ) : tagList.length === 0 ? (
          <span className="qms-tags-input__empty">—</span>
        ) : null}
      </div>
      {!disabled && hint ? <p className="qms-tags-input__hint">{hint}</p> : null}
    </div>
  );
}

export default QmsTagsInput;
